/**
 * OAuth Service Types
 *
 * Core types for the OAuth connection architecture.
 * Service = what we connect to (GMAIL, GOOGLE_DRIVE, etc.)
 * IdentityProvider = who issues the token (GOOGLE, MICROSOFT, NOTION)
 *
 * ADDING A NEW SERVICE:
 * 1. Add to Prisma OAuthService enum
 * 2. Add entry to SERVICE_CONFIG below
 * That's it. All types auto-derive.
 */

// =============================================================================
// SERVICE_CONFIG - Single Source of Truth
// =============================================================================

/**
 * Configuration for each OAuth service.
 * Add new services here - types derive automatically.
 *
 * @property provider - The identity provider (who issues OAuth tokens)
 * @property category - 'document' for storage, 'deployment' for messaging
 * @property subcategory - For deployments: 'email' or 'chat'
 */
export const SERVICE_CONFIG = {
  // Email services (deployment subcategory)
  GMAIL: { provider: "GOOGLE", category: "deployment", subcategory: "email" },
  OUTLOOK: { provider: "MICROSOFT", category: "deployment", subcategory: "email" },
  // Chat services (deployment subcategory)
  TEAMS_TENANT: { provider: "MICROSOFT", category: "deployment", subcategory: "chat" },
  // Spreadsheet services (deployment subcategory)
  GOOGLE_SHEETS: { provider: "GOOGLE", category: "deployment", subcategory: "spreadsheet" },
  // Document storage services (no subcategory)
  GOOGLE_DRIVE: { provider: "GOOGLE", category: "document", subcategory: null },
  SHAREPOINT: { provider: "MICROSOFT", category: "document", subcategory: null },
  NOTION: { provider: "NOTION", category: "document", subcategory: null }
} as const;

// =============================================================================
// Derived Types (auto-generated from SERVICE_CONFIG)
// =============================================================================

/** All OAuth services */
export type OAuthService = keyof typeof SERVICE_CONFIG;

/** Identity providers (who issues the OAuth token) */
export type OAuthIdentityProvider = (typeof SERVICE_CONFIG)[OAuthService]["provider"];

/** Subcategory type (email, chat, or null for documents) */
export type ServiceSubcategory = (typeof SERVICE_CONFIG)[OAuthService]["subcategory"];

/** Extract services by category */
type ExtractByCategory<Category extends "document" | "deployment"> = {
  [K in OAuthService]: (typeof SERVICE_CONFIG)[K]["category"] extends Category ? K : never;
}[OAuthService];

/** Extract services by subcategory */
type ExtractBySubcategory<Subcategory extends "email" | "chat" | "spreadsheet"> = {
  [K in OAuthService]: (typeof SERVICE_CONFIG)[K]["subcategory"] extends Subcategory ? K : never;
}[OAuthService];

/** Document storage services (Google Drive, SharePoint, Notion) */
export type DocStorageService = ExtractByCategory<"document">;

/** Deployment/messaging services (Gmail, Outlook, Slack, Teams) */
export type DeploymentService = ExtractByCategory<"deployment">;

/** Email services (Gmail, Outlook) */
export type EmailService = ExtractBySubcategory<"email">;

/** Chat services (Slack, Teams) */
export type ChatService = ExtractBySubcategory<"chat">;

/** Spreadsheet services (Google Sheets) */
export type SpreadsheetService = ExtractBySubcategory<"spreadsheet">;

// =============================================================================
// Runtime Arrays (auto-derived for iteration, dropdowns, etc.)
// =============================================================================

/** All OAuth services */
export const ALL_OAUTH_SERVICES = Object.keys(SERVICE_CONFIG) as OAuthService[];

/** All identity providers (deduplicated) */
export const ALL_IDENTITY_PROVIDERS = [
  ...new Set(Object.values(SERVICE_CONFIG).map((c) => c.provider))
] as OAuthIdentityProvider[];

/** Document storage services */
export const DOC_STORAGE_SERVICES = ALL_OAUTH_SERVICES.filter(
  (s) => SERVICE_CONFIG[s].category === "document"
) as DocStorageService[];

/** Deployment services */
export const DEPLOYMENT_SERVICES = ALL_OAUTH_SERVICES.filter(
  (s) => SERVICE_CONFIG[s].category === "deployment"
) as DeploymentService[];

/** Email services */
export const EMAIL_SERVICES = ALL_OAUTH_SERVICES.filter(
  (s) => SERVICE_CONFIG[s].subcategory === "email"
) as EmailService[];

/** Chat services */
export const CHAT_SERVICES = ALL_OAUTH_SERVICES.filter(
  (s) => SERVICE_CONFIG[s].subcategory === "chat"
) as ChatService[];

/** Spreadsheet services */
export const SPREADSHEET_SERVICES = ALL_OAUTH_SERVICES.filter(
  (s) => SERVICE_CONFIG[s].subcategory === "spreadsheet"
) as SpreadsheetService[];

// =============================================================================
// Helper Functions
// =============================================================================

/**
 * Get the identity provider for a service.
 * Provider is derived from SERVICE_CONFIG, never stored.
 */
export function getIdentityProvider(service: OAuthService): OAuthIdentityProvider {
  return SERVICE_CONFIG[service].provider;
}

/**
 * Get all services for a given identity provider.
 */
export function getServicesForProvider(provider: OAuthIdentityProvider): OAuthService[] {
  return ALL_OAUTH_SERVICES.filter((s) => SERVICE_CONFIG[s].provider === provider);
}

/** Type guard: is this a document storage service? */
export function isDocStorageService(service: OAuthService): service is DocStorageService {
  return SERVICE_CONFIG[service].category === "document";
}

/** Type guard: is this a deployment service? */
export function isDeploymentService(service: OAuthService): service is DeploymentService {
  return SERVICE_CONFIG[service].category === "deployment";
}

/** Type guard: is this an email service? */
export function isEmailService(service: OAuthService): service is EmailService {
  return SERVICE_CONFIG[service].subcategory === "email";
}

/** Type guard: is this a chat service? */
export function isChatService(service: OAuthService): service is ChatService {
  return SERVICE_CONFIG[service].subcategory === "chat";
}

/** Type guard: is this a spreadsheet service? */
export function isSpreadsheetService(service: OAuthService): service is SpreadsheetService {
  return SERVICE_CONFIG[service].subcategory === "spreadsheet";
}
